// Base
import React, { useEffect, useRef, useState, useContext } from "react";
import PropTypes from "prop-types";
import requiredIf from "react-required-if";
import cx from "classnames";
import { EqualHeightContext } from "./EqualHeightContext";

import styles from "./EqualHeight.scss";

export function EqualHeightElement(props) {
  const {
    children,
    name,
    placeholder,
    overflow,
    className,
    ...restProps
  } = props;
  const { sizes, setSizes, animate } = useContext(EqualHeightContext);
  const [height, setHeight] = useState(undefined);
  const [innerHeight, setInnerHeight] = useState(undefined);
  const element = useRef(null);
  const innerElement = useRef(null);

  const findSize = () => sizes.findIndex((e) => e.name === name);

  const getSavedHeight = () => {
    const sizesIndex = findSize();
    if (sizesIndex >= 0) {
      return sizes[sizesIndex].height;
    }
    return null;
  };

  const addHeight = () => {
    const elementHeight = innerElement.current.clientHeight;
    setInnerHeight(elementHeight);
    const savedHeight = getSavedHeight();
    if (typeof savedHeight === "number") {
      if (savedHeight < elementHeight) {
        const sizesIndex = findSize();
        sizes[sizesIndex].height = elementHeight;
        const newSizes = [...sizes];
        setSizes(newSizes);
      }
    } else {
      sizes.push({
        name,
        height: elementHeight,
      });
      const newSizes = [...sizes];
      setSizes(newSizes);
    }
  };

  const removeHeight = () => {
    const sizesIndex = findSize();
    if (sizesIndex >= 0) {
      sizes.splice(sizesIndex, 1);
      const newSizes = [...sizes];
      setSizes(newSizes);
    }
  };

  useEffect(() => {
    addHeight();
  }, [sizes]);

  useEffect(() => {
    if (typeof ResizeObserver === "undefined") {
      return () => {};
    }
    const resizeObserver = new ResizeObserver(() => {
      if (!innerElement.current) return;
      const elementHeight = innerElement.current.clientHeight;
      if (innerHeight !== elementHeight) {
        setInnerHeight(elementHeight);
        removeHeight();
        addHeight();
      }
    });
    resizeObserver.observe(innerElement.current);
    return () => {
      if (!innerElement.current) return;
      resizeObserver.unobserve(innerElement.current);
    };
  }, [sizes]);

  // Set height on local element
  useEffect(() => {
    setHeight(getSavedHeight());
  }, [sizes]);

  const style = { ...restProps.style };
  if (typeof height === "number" && height > 0) {
    if (animate) {
      style.minHeight = `${height}px`;
    } else {
      style.height = `${height}px`;
    }
  }
  const classes = cx(styles.holder, {
    [styles.animate]: animate,
    [styles.overflow]: overflow,
    [className]: className,
  });

  return (
    <div ref={element} className={classes} {...restProps} style={style}>
      <div ref={innerElement} className={styles.inner}>
        {!placeholder && children}
      </div>
    </div>
  );
}

EqualHeightElement.propTypes = {
  children: requiredIf(
    PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
    (props) => !props.placeholder
  ),
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.bool,
  overflow: PropTypes.bool,
  className: PropTypes.string,
};

EqualHeightElement.defaultProps = {
  children: "",
  placeholder: false,
  overflow: true,
  className: "",
};

EqualHeightElement.displayName = "EqualHeightElement";

export default EqualHeightElement;
